import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  MapPin, 
  Briefcase, 
  Clock,
  Building2,
  Loader2,
  Home as HomeIcon,
  Filter,
  ShieldOff,
  ShieldCheck
} from 'lucide-react';
import { getJobs, blockJob, unblockJob, getBlockedJobs } from '../services/api';
import { toast } from 'sonner';

const JobList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [blockedIds, setBlockedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [blockingId, setBlockingId] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [jobType, setJobType] = useState(searchParams.get('type') || '');
  const [remote, setRemote] = useState(searchParams.get('remote') || '');

  useEffect(() => {
    fetchJobs();
    fetchBlocked();
  }, [searchParams]);

  const fetchBlocked = async () => {
    try {
      const res = await getBlockedJobs();
      setBlockedIds(new Set(res.data.map(b => b.job_id)));
    } catch {}
  };

  const handleBlock = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    setBlockingId(jobId);
    try {
      if (blockedIds.has(jobId)) {
        await unblockJob(jobId);
        setBlockedIds(prev => { const s = new Set(prev); s.delete(jobId); return s; });
        toast.success('Blockierung aufgehoben');
      } else {
        await blockJob(jobId);
        setBlockedIds(prev => new Set(prev).add(jobId));
        toast.success('Stelle blockiert');
      }
    } catch (error) {
      toast.error('Fehler');
    } finally {
      setBlockingId(null);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchParams.get('search') || undefined,
        location: searchParams.get('location') || undefined,
        job_type: searchParams.get('type') || undefined,
        remote: searchParams.get('remote') === 'true' ? true : undefined
      };
      const res = await getJobs(params);
      setJobs(res.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (location) params.set('location', location);
    if (jobType) params.set('type', jobType);
    if (remote) params.set('remote', remote);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setLocation('');
    setJobType('');
    setRemote('');
    setSearchParams(new URLSearchParams());
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="job-list-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Jobs finden</h1>
          <p className="text-muted-foreground mt-1">
            {jobs.length} Stellenangebote gefunden
          </p>
        </div>

        {/* Search & Filters */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="relative md:col-span-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Jobtitel, Fähigkeiten..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="job-search-input"
                  />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Ort..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="pl-10"
                    data-testid="job-location-input"
                  />
                </div>
                <Button type="submit" className="rounded-full" data-testid="job-search-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Suchen
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Select value={jobType} onValueChange={setJobType}>
                  <SelectTrigger className="w-40" data-testid="job-type-filter">
                    <Briefcase className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Jobtyp" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vollzeit">Vollzeit</SelectItem>
                    <SelectItem value="Teilzeit">Teilzeit</SelectItem>
                    <SelectItem value="Minijob">Minijob</SelectItem>
                    <SelectItem value="Praktikum">Praktikum</SelectItem>
                    <SelectItem value="Freelance">Freelance</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={remote} onValueChange={setRemote}>
                  <SelectTrigger className="w-40" data-testid="remote-filter">
                    <HomeIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Remote" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Remote möglich</SelectItem>
                    <SelectItem value="false">Vor Ort</SelectItem>
                  </SelectContent>
                </Select>

                {(search || location || jobType || remote) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="text-muted-foreground"
                  >
                    <Filter className="w-4 h-4 mr-1" />
                    Filter zurücksetzen
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Job List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : jobs.filter(j => !blockedIds.has(j.job_id)).length > 0 ? (
          <div className="grid gap-4">
            {jobs.filter(j => !blockedIds.has(j.job_id)).map((job) => (
              <div key={job.job_id} className="relative" data-testid={`job-card-${job.job_id}`}>
                <Link to={`/jobs/${job.job_id}`}>
                  <Card className="hover:shadow-md hover:border-primary/20 transition-all cursor-pointer">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="font-semibold text-foreground text-lg">{job.title}</h3>
                              <p className="text-muted-foreground">{job.company_name || 'Unternehmen'}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {job.remote_option && (
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium whitespace-nowrap">
                                  Remote möglich
                                </span>
                              )}
                            </div>
                          </div>
                        
                          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {job.job_type}
                            </span>
                            {job.salary_range && (
                              <span className="flex items-center gap-1">
                                {job.salary_range}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(job.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>

                          {job.requirements && job.requirements.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {job.requirements.slice(0, 4).map((req, idx) => (
                                <span 
                                  key={idx}
                                  className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-xs"
                                >
                                  {req}
                                </span>
                              ))}
                              {job.requirements.length > 4 && (
                                <span className="px-2 py-1 text-muted-foreground text-xs">
                                  +{job.requirements.length - 4} mehr
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                <button
                  onClick={(e) => handleBlock(e, job.job_id)}
                  disabled={blockingId === job.job_id}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors z-10"
                  title="Stelle blockieren"
                  data-testid={`block-btn-${job.job_id}`}
                >
                  {blockingId === job.job_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldOff className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Keine Jobs gefunden</h3>
              <p className="text-muted-foreground">
                Versuchen Sie andere Suchbegriffe oder Filter
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default JobList;
